import { motion, useReducedMotion } from "framer-motion";

import {
  DURATION,
  EASE_OUT,
  VIEWPORT,
  transitions,
} from "./motionPresets";

/**
 * A card that reveals on scroll *and* reacts to pointer.
 *
 * Reveal and hover live in one variant set, so hovering mid-reveal can't strand
 * the card halfway: unhovering always returns it to `visible`.
 *
 * Because the hover state is a variant (not a CSS class), any nested
 * <motion.*> element — e.g. the image wrapper using `imageZoom` — picks the
 * gesture up automatically through framer's variant propagation.
 */
const HoverCard = ({
  as = "div",
  children,
  className,
  y = 26,
  lift = -6,
  duration = DURATION.base,
  standalone = false, // true when not inside a <Stagger>
  delay = 0,
  viewport,
  ...rest
}) => {
  const shouldReduce = useReducedMotion();
  const Tag = motion[as] || motion.div;

  const variants = shouldReduce
    ? {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { duration: DURATION.fast, delay } },
        hover: {},
        tap: {},
      }
    : {
        hidden: { opacity: 0, y },
        visible: {
          opacity: 1,
          y: 0,
          transition: { duration, ease: EASE_OUT, delay },
        },
        hover: { y: lift, transition: transitions.fast },
        tap: { scale: 0.99, transition: { duration: 0.12 } },
      };

  const revealProps = standalone
    ? {
        initial: "hidden",
        whileInView: "visible",
        viewport: { ...VIEWPORT, ...viewport },
      }
    : {};

  return (
    <Tag
      className={className}
      variants={variants}
      whileHover="hover"
      whileTap="tap"
      {...revealProps}
      {...rest}
    >
      {children}
    </Tag>
  );
};

export default HoverCard;
